import {FC, useMemo} from "react";
import {Badge, Box, LinearProgress, Typography} from "@mui/material";
import {
	DataGridPro,
	getGridNumericOperators,
	getGridSingleSelectOperators,
	getGridStringOperators,
	GridColDef,
	GridInitialState,
	GridRowParams,
	GRID_CHECKBOX_SELECTION_COL_DEF,
	GridValueGetterParams,
	type GridFilterPanelProps
} from "@mui/x-data-grid-pro";
import {makeStyles} from "tss-react/mui";
import {useAnnouncementsListGrid} from "./hooks/useAnnouncementsListGrid";
import {AnnouncementsListGridToolbar} from "./Toolbar/AnnouncementsListGridToolbar";
import {useNavigate} from "react-router-dom";
import type {AdminAnnouncementListItem} from "@/app2/shared/data";
import {
	formatDeliveryDateTimeDisplay,
	getAnnouncementAdminDisplayStatus,
	getAnnouncementEffectiveDeliveryDate
} from "./announcementAdminStatus";
import {
	serverSideDateTimeFilterOperators,
	serverSideDateTimeFilterPanelProps
} from "@/app2/shared/dataGrid/serverSideDateTimeColumnFilters";

/** Server-side filters only; client `getApplyFilterFn` must be disabled. */
const serverSideStringOperators = getGridStringOperators()
	.filter((op) => op.value !== "isAnyOf")
	.map((op) => ({
		...op,
		getApplyFilterFn: () => null
	}));

const serverSideSingleSelectOperators = getGridSingleSelectOperators()
	.filter((op) => op.value !== "isAnyOf")
	.map((op) => ({
		...op,
		getApplyFilterFn: () => null
	}));

const serverSideNumericOperators = getGridNumericOperators()
	.filter((op) => op.value !== "isAnyOf")
	.map((op) => ({
		...op,
		getApplyFilterFn: () => null
	}));

const useStyles = makeStyles()((theme) => ({
	dataGrid: {
		"& th": {
			fontSize: "11px",
			fontWeight: 700,
			textTransform: "uppercase"
		},
		"& svg path": {
			fill: theme.palette.common.white
		}
	},
	tableBadge: {
		padding: theme.spacing(0.2, 1.25)
	},
	readDistribution: {
		width: "100%",
		display: "flex",
		gap: theme.spacing(2),
		"& > p": {width: theme.spacing(4), textAlign: "center"},
		"& > div": {
			width: "100%",
			display: "flex",
			flexDirection: "column",
			justifyContent: "center"
		}
	}
}));

const announcementsFilterPanelProps: GridFilterPanelProps = {
	...serverSideDateTimeFilterPanelProps
};

export function ReadDistributionCell({
	row,
	classes
}: {
	row: AdminAnnouncementListItem;
	classes: ReturnType<typeof useStyles>["classes"];
}) {
	const progress = row.audienceCount
		? Math.round((row.readCount / row.audienceCount) * 100)
		: 0;
	return (
		<Box className={classes.readDistribution}>
			<Box>
				<LinearProgress sx={{width: "100%"}} value={progress} variant="determinate" />
			</Box>
			<Typography>{progress}%</Typography>
		</Box>
	);
}

function StatusBadge({row}: { row: AdminAnnouncementListItem }) {
	const {classes} = useStyles();
	const status = getAnnouncementAdminDisplayStatus(row);

	if (status === "inactive") {
		return (
			<Badge className={classes.tableBadge} color="info">
				Inactive
			</Badge>
		);
	}
	if (status === "draft") {
		return (
			<Badge className={classes.tableBadge} color="info">
				Draft
			</Badge>
		);
	}
	if (status === "scheduled") {
		return (
			<Badge className={classes.tableBadge} color="warning">
				Scheduled
			</Badge>
		);
	}
	if (status === "processing") {
		return (
			<Badge className={classes.tableBadge} color="warning">
				Processing
			</Badge>
		);
	}
	return (
		<Badge className={classes.tableBadge} color="success">
			Live
		</Badge>
	);
}

export const AnnouncementsListGrid: FC = () => {
	const {classes} = useStyles();
	const navigate = useNavigate();
	const {
		rows,
		rowCount,
		isInitialLoad,
		isFetching,
		page,
		pageSize,
		onPageChange,
		onPageSizeChange,
		onFilterChange,
		onSortModelChange,
		sortModel,
		initialFilterModel
	} = useAnnouncementsListGrid();

	// Restore filter chips/inputs on mount when the user comes back to the page.
	const initialState: GridInitialState | undefined = useMemo(
		() =>
			initialFilterModel
				? {filter: {filterModel: initialFilterModel}}
				: undefined,
		[initialFilterModel]
	);

	const columns: GridColDef<AdminAnnouncementListItem>[] = useMemo(
		() => [
			{
				...GRID_CHECKBOX_SELECTION_COL_DEF,
				type: "string",
				hideable: false
			},
			{
				field: "title",
				type: "string",
				flex: 1,
				headerName: "Title",
				minWidth: 240,
				filterOperators: serverSideStringOperators
			},
			{
				field: "scheduledDeliveryDate",
				type: "dateTime",
				flex: 1,
				headerName: "Delivery date",
				// MUI v5 valueFormatter has no `row`; valueGetter supplies Date via getAnnouncementEffectiveDeliveryDate (same rules as formatAnnouncementDeliveryDateTime).
				valueGetter: (params: GridValueGetterParams<AdminAnnouncementListItem>) =>
					getAnnouncementEffectiveDeliveryDate(params.row),
				valueFormatter: (params) =>
					params.value ? formatDeliveryDateTimeDisplay(new Date(params.value as Date)) : "",
				filterOperators: serverSideDateTimeFilterOperators
			},
			{
				field: "_status",
				type: "singleSelect",
				flex: 1,
				headerName: "Status",
				valueOptions: [
					{value: "inactive", label: "Inactive"},
					{value: "draft", label: "Draft"},
					{value: "scheduled", label: "Scheduled"},
					{value: "processing", label: "Processing"},
					{value: "live", label: "Live"}
				],
				valueGetter: (params: GridValueGetterParams<AdminAnnouncementListItem>) =>
					getAnnouncementAdminDisplayStatus(params.row),
				renderCell: (params) => <StatusBadge row={params.row} />,
				filterOperators: serverSideSingleSelectOperators
			},
			{
				field: "author",
				type: "string",
				flex: 1,
				headerName: "Author",
				valueGetter: (params: GridValueGetterParams<AdminAnnouncementListItem>) =>
					params.row.author ?? "",
				filterOperators: serverSideStringOperators
			},
			{
				field: "_read",
				type: "number",
				flex: 1,
				headerName: "Read Distribution",
				valueGetter: (params: GridValueGetterParams<AdminAnnouncementListItem>) =>
					params.row.audienceCount
						? Math.round((params.row.readCount / params.row.audienceCount) * 100)
						: 0,
				renderCell: (params) => (
					<ReadDistributionCell row={params.row} classes={classes} />
				),
				filterOperators: serverSideNumericOperators
			}
		],
		[classes]
	);

	const handleRowClick = (params: GridRowParams<AdminAnnouncementListItem>) => {
		navigate(String(params.id));
	};

	const Toolbar = (): JSX.Element => <AnnouncementsListGridToolbar />;

	if (isInitialLoad) {
		return <LinearProgress />;
	}

	return (
		<Box>
			<DataGridPro<AdminAnnouncementListItem>
				autoHeight
				className={classes.dataGrid}
				loading={isFetching}
				sortingMode="server"
				page={page}
				pageSize={pageSize}
				onPageSizeChange={onPageSizeChange}
				onPageChange={onPageChange}
				checkboxSelection
				paginationMode="server"
				pagination
				rowCount={rowCount}
				sortModel={sortModel}
				onSortModelChange={onSortModelChange}
				rows={rows}
				columns={columns}
				filterMode="server"
				onFilterModelChange={onFilterChange}
				initialState={initialState}
				components={{Toolbar}}
				componentsProps={{filterPanel: announcementsFilterPanelProps}}
				rowsPerPageOptions={[25, 50, 100, 200]}
				onRowClick={handleRowClick}
				getRowId={(row) => row.id}
				sx={{
					"& .MuiDataGrid-row": {cursor: "pointer"}
				}}
			/>
		</Box>
	);
};
